import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { defaultLocale } from 'dibs-intl/exports/constants';
import { Link } from 'dibs-elements/exports/Link';
import classnames from 'classnames';
import {
    getTradeBuyerProtectionDuration,
    getTradeBuyerProtectionTier,
    getTradeUserRewardsProgramInfo,
} from '../helpers/tradeBuyerProtectionHelper';
import styles from './PdpSharedBuyerProtectionBox.scss';

export const PdpSharedBuyerProtectionBoxComponent = ({ viewer, user, item }) => {
    const { locale } = useIntl();
    const { isAuctionEnabled } = item || {};
    const { isIntlFeaturesEnabled } = viewer || {};
    const readMoreHref = user?.isVerifiedTrade ? '/info/trade/promise/' : '/about/promise/';
    const {
        tradeProtectionTierCode,
        tradeProtectionDurationCode,
        showTradeRewardsCopy,
    } = getTradeUserRewardsProgramInfo(user);

    return (
        <div className={styles.wrapper} data-tn="buyer-protection-wrrapper">
            {isAuctionEnabled && (
                <div className={styles.copy}>
                    <div className={styles.title}>
                        <FormattedMessage
                            id="PdpSharedBuyerProtectionBox.auction.title"
                            defaultMessage="Auctions on 1stDibs"
                        />
                    </div>
                    <div className={styles.message}>
                        <FormattedMessage
                            id="PdpSharedBuyerProtectionBox.auction.message"
                            defaultMessage="Our 7-day auctions are an opportunity to bid on extraordinary design. We do not charge a Buyer's Premium and shipping is facilitated by 1stDibs and/or the seller. Plus, all auction purchases are covered by our comprehensive Buyer Protection. <link>Learn More</link>"
                            values={{
                                link: chunks => (
                                    <Link href="/info/auctions-faq/" target="_blank">
                                        {chunks}
                                    </Link>
                                ),
                            }}
                        />
                    </div>
                </div>
            )}
            <div className={styles.copy}>
                <div className={styles.title} data-tn="buyer-protection-title">
                    <FormattedMessage
                        id="PdpSharedBuyerProtectionBox.title"
                        defaultMessage="1stDibs Buyer Protection Guaranteed"
                    />
                </div>
                <div
                    className={classnames(styles.message, {
                        [styles.tradeRewardsMessage]: showTradeRewardsCopy,
                    })}
                    data-tn="buyer-protection-message"
                >
                    {' '}
                    {showTradeRewardsCopy ? (
                        <FormattedMessage
                            id="PdpSharedBuyerProtectionBox.tradeRewardsMessage"
                            defaultMessage="If your item arrives not as described, we’ll work with you and the seller to make it right. As a {tier}, you get {days} to report any issues you have with the order."
                            values={{
                                tier: (
                                    <span className={styles.messageBold}>
                                        {getTradeBuyerProtectionTier(tradeProtectionTierCode)}
                                    </span>
                                ),
                                days: (
                                    <span className={styles.messageBold}>
                                        {getTradeBuyerProtectionDuration(
                                            tradeProtectionDurationCode
                                        )}
                                    </span>
                                ),
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="PdpSharedBuyerProtectionBox.message"
                            defaultMessage="If your item arrives not as described, we’ll work with you and the seller to make it right."
                        />
                    )}{' '}
                    <Link
                        href={readMoreHref}
                        ariaLabel={
                            <FormattedMessage
                                id="PdpSharedBuyerProtectionBox.readMoreLinkA11y"
                                defaultMessage="Learn more about buyer protection"
                            />
                        }
                        target="_blank"
                        dataTn="buyer-protection-link"
                    >
                        <FormattedMessage
                            id="PdpSharedBuyerProtectionBox.readMoreLink"
                            defaultMessage="Learn more"
                        />
                    </Link>
                </div>
            </div>
            {isIntlFeaturesEnabled && locale !== defaultLocale && (
                <div className={styles.copy}>
                    <div className={styles.message}>
                        <FormattedMessage
                            id="PdpSharedBuyerProtectionBox.translation.message"
                            defaultMessage="Parts of this page have been automatically translated. 1stDibs cannot guarantee the accuracy of translations. English is the default language of this website."
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

PdpSharedBuyerProtectionBoxComponent.propTypes = {
    viewer: PropTypes.object,
    user: PropTypes.object,
    item: PropTypes.object,
};

export const PdpSharedBuyerProtectionBox = createFragmentContainer(
    PdpSharedBuyerProtectionBoxComponent,
    {
        viewer: graphql`
            fragment PdpSharedBuyerProtectionBox_viewer on Viewer {
                isIntlFeaturesEnabled: featureFlag(feature: "intlMVP2022")
            }
        `,
        item: graphql`
            fragment PdpSharedBuyerProtectionBox_item on Item {
                isAuctionEnabled
            }
        `,
        user: graphql`
            fragment PdpSharedBuyerProtectionBox_user on User {
                isVerifiedTrade
                tradeFirm @include(if: $isTrade) {
                    rewards {
                        currentProgram {
                            programInfo {
                                code
                            }
                            childPrograms(filter: "ENHANCED_BUYER_PROTECTION") {
                                code
                            }
                        }
                    }
                }
            }
        `,
    }
);
